import { validateEventGeometry, type EventGeometry, type HazardCategory } from "@shared/eventGeography";
import type { NormalizedSignal, SignalConfidence } from "./types";
import { EMPTY_COUNTRY_BOUNDARIES, type CountryBoundaryIndex, whoCountryBoundary } from "./whoCountryBoundaries";

const LEGACY_HAZARD_CATEGORY: Record<string, HazardCategory> = {
  earthquake: "seismic",
  weather: "weather",
  health: "health",
  geopolitics: "geopolitical",
  "market-news": "infrastructure-supply-chain",
  "supply-chain": "infrastructure-supply-chain",
};

export function signalHazardCategory(signal: NormalizedSignal): HazardCategory | undefined {
  return signal.hazardCategory ?? LEGACY_HAZARD_CATEGORY[signal.category];
}

export function signalMapGeometry(
  signal: NormalizedSignal,
  countryBoundaries: CountryBoundaryIndex = EMPTY_COUNTRY_BOUNDARIES,
): EventGeometry | undefined {
  if (signal.geography?.geometry) return validateEventGeometry(signal.geography.geometry);
  const countryBoundary = whoCountryBoundary(signal, countryBoundaries);
  if (countryBoundary) return countryBoundary;
  if (signal.geography) return undefined;
  return validateEventGeometry({ type: "Point", coordinates: [signal.lng, signal.lat] });
}

export function isLocationOnlySignal(signal: NormalizedSignal) {
  const geography = signal.geography;
  return Boolean(
    geography
    && !geography.geometry
    && geography.location
    && !["point", "area", "unknown"].includes(geography.scope),
  );
}

export function availableHazardLayers(signals: NormalizedSignal[]) {
  return [...new Set(signals
    .filter((signal) => signalMapGeometry(signal) || isLocationOnlySignal(signal))
    .map(signalHazardCategory)
    .filter((category): category is HazardCategory => Boolean(category)))]
    .sort();
}

export function filterMapSignals(
  signals: NormalizedSignal[],
  enabledLayers: Set<string>,
  showStale: boolean,
  minConfidence: SignalConfidence,
) {
  const confidenceOrder: Record<SignalConfidence, number> = { high: 0, medium: 1, low: 2 };
  return signals.filter((signal) => {
    const category = signalHazardCategory(signal);
    if (!category || !enabledLayers.has(category)) return false;
    if (!showStale && ["stale", "fallback", "stub", "error"].includes(signal.status)) return false;
    if (confidenceOrder[signal.confidence] > confidenceOrder[minConfidence]) return false;
    return true;
  });
}
